import { createContext, useContext, useReducer, useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import {
    getSuccessfulExecutions,
    getTestCase,
    saveExpertSession,
    getExpertSessionByCode,
    getExpertSessionByDevice,
    calculateIQAI,
    type IQAIRatings as FirestoreIQAIRatings,
    type ExpertSession as FirestoreExpertSession,
} from '../services/firestore-proxy';
import {
    getStableFingerprint,
    createDeviceRecord,
    updateDeviceRecord,
    type DeviceRecord,
} from '../services/device-fingerprint';

// Types
export interface IQAIRatings {
    clarity: number;      // 1-5
    adequacy: number;     // 1-5
    consistency: number;  // 1-5
    accessibility: number;// 1-5
    usability: number;    // 1-5
}

export interface IQAIEvaluation {
    execution_id: string;
    profile_id: string;
    task_type: string;
    process_phase: string;
    ratings: IQAIRatings;
    iqai_score: number; // Calculated automatically
    comments?: string;
    evaluated_at: string;
    time_spent_seconds: number; // Time spent on this interface
    interaction_metadata?: {
        tabs_viewed: string[];
        tab_times: { persona: number; task: number; interface: number };
        scroll_depth_percent: number;
        click_count: number;
        rating_changes: Array<{ dimension: string; from: number; to: number; at: string }>;
        dimension_order: string[];
        zoom_level: number;
        fullscreen_used: boolean;
    };
}

export interface ExpertProfile {
    // Identificação (opcional para acesso anônimo)
    name?: string;
    email?: string;

    // Critérios de Inclusão
    expertise:
    | 'ux_ui_design'
    | 'product_management'
    | 'frontend_dev'
    | 'accessibility'
    | 'other';
    other_expertise?: string;
    years_experience: '<1' | '1-2' | '2-5' | '5-10' | '>10';

    // Caracterização Demográfica (não exclusão)
    gender: 'female' | 'male' | 'non_binary' | 'prefer_not_say';
    age_range: '18-25' | '26-35' | '36-45' | '46-55' | '56+';
    accessibility_need: 'none' | 'visual' | 'auditory' | 'motor' | 'cognitive' | 'other';
    other_accessibility?: string;
    education: 'undergrad_incomplete' | 'undergrad_complete' | 'specialization' | 'masters' | 'doctorate';

    // Calculado
    is_qualified?: boolean;
}

// Função para verificar se especialista atende critérios de inclusão
export function isQualifiedExpert(profile: ExpertProfile): boolean {
    const validExpertise = [
        'ux_ui_design',
        'product_management',
        'frontend_dev',
        'backend_dev',
        'qa_quality',
        'business_analysis',
        'information_architecture',
        'accessibility'
    ];
    const validExperience = ['1-2', '2-5', '5-10', '>10'];

    return validExpertise.includes(profile.expertise) &&
        validExperience.includes(profile.years_experience);
}

export interface InterfaceToEvaluate {
    execution_id: string;
    profile_id: string;
    task_type: string;
    process_phase: string;
    ui_spec: object;           // Default/original spec
    ui_spec_pt?: object;       // Portuguese version (optional)
    ui_spec_en?: object;       // English version (optional)
}

// Draft for auto-saving evaluation in progress
export interface EvaluationDraft {
    interface_index: number;
    ratings: Partial<IQAIRatings>;
    comments?: string;
    last_updated: string;
}

export interface ExpertSession {
    expert_id: string;
    recovery_code: string; // Código curto para recuperação (6 caracteres)
    devices: DeviceRecord[]; // Multi-device support
    profile: Partial<ExpertProfile>; // Changed to Partial for incremental save
    profile_status: 'incomplete' | 'complete'; // Track profile completion
    interfaces_to_evaluate: InterfaceToEvaluate[];
    current_index: number;
    evaluations: IQAIEvaluation[];
    current_evaluation_draft?: EvaluationDraft; // DEPRECATED: kept for backward compatibility during migration
    evaluation_drafts?: Record<number, EvaluationDraft>; // Auto-save drafts per interface (keyed by interface_index)
    status: 'in_progress' | 'completed' | 'abandoned';
    started_at: string;
    last_activity_at: string;
    completed_at?: string;
    metadata: {
        app_version: string;
        created_from: 'new' | 'recovered' | 'provisional';
        recovery_count: number;
        last_device_fingerprint: string;
    };
}

// Gera código de recuperação amigável (6 caracteres alfanuméricos)
function generateRecoveryCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Evita 0/O, 1/I/L
    let code = '';
    for (let i = 0; i < 6; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
}

interface ExpertState {
    session: ExpertSession | null;
    isLoading: boolean;
    error: string | null;
    initialized: boolean; // Indicates if localStorage restoration has been attempted
}

type ExpertAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: string | null }
    | { type: 'SET_INITIALIZED'; payload: boolean }
    | { type: 'START_SESSION'; payload: ExpertSession }
    | { type: 'UPDATE_PROFILE'; payload: Partial<ExpertProfile> }
    | { type: 'SET_PROFILE_COMPLETE'; payload: ExpertProfile }
    | { type: 'UPDATE_EVALUATION_DRAFT'; payload: EvaluationDraft }
    | { type: 'CLEAR_EVALUATION_DRAFT' }
    | { type: 'ADD_EVALUATION'; payload: IQAIEvaluation }
    | { type: 'NEXT_INTERFACE' }
    | { type: 'PREV_INTERFACE' }
    | { type: 'END_SESSION' };

const STORAGE_KEY = 'lumen_expert_session';

function expertReducer(state: ExpertState, action: ExpertAction): ExpertState {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload, isLoading: false };
        case 'SET_INITIALIZED':
            return { ...state, initialized: action.payload };
        case 'START_SESSION':
            return { ...state, session: action.payload, isLoading: false, error: null, initialized: true };
        case 'UPDATE_PROFILE':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    profile: { ...state.session.profile, ...action.payload },
                    last_activity_at: new Date().toISOString()
                }
            };
        case 'SET_PROFILE_COMPLETE':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    profile: action.payload,
                    profile_status: 'complete',
                    last_activity_at: new Date().toISOString()
                }
            };
        case 'UPDATE_EVALUATION_DRAFT': {
            if (!state.session) return state;
            // Store draft in the evaluation_drafts map, keyed by interface_index
            const draftIndex = action.payload.interface_index;
            return {
                ...state,
                session: {
                    ...state.session,
                    evaluation_drafts: {
                        ...state.session.evaluation_drafts,
                        [draftIndex]: action.payload
                    },
                    last_activity_at: new Date().toISOString()
                }
            };
        }
        case 'CLEAR_EVALUATION_DRAFT': {
            if (!state.session) return state;
            // Clear draft for the current interface only
            const currentIdx = state.session.current_index;
            const { [currentIdx]: _removed, ...remainingDrafts } = state.session.evaluation_drafts || {};
            return {
                ...state,
                session: {
                    ...state.session,
                    evaluation_drafts: remainingDrafts,
                    current_evaluation_draft: undefined // Also clear legacy field
                }
            };
        }
        case 'ADD_EVALUATION': {
            if (!state.session) return state;
            // Check if evaluation for this execution_id already exists
            const existingEvalIndex = state.session.evaluations.findIndex(
                ev => ev.execution_id === action.payload.execution_id
            );
            let updatedEvaluations: IQAIEvaluation[];
            if (existingEvalIndex >= 0) {
                // Update existing evaluation
                updatedEvaluations = [...state.session.evaluations];
                updatedEvaluations[existingEvalIndex] = action.payload;
            } else {
                // Add new evaluation
                updatedEvaluations = [...state.session.evaluations, action.payload];
            }
            // Clear only the draft for the current interface being submitted
            const submittedIdx = state.session.current_index;
            const { [submittedIdx]: _removedDraft, ...remainingEvalDrafts } = state.session.evaluation_drafts || {};
            return {
                ...state,
                session: {
                    ...state.session,
                    evaluations: updatedEvaluations,
                    evaluation_drafts: remainingEvalDrafts,
                    current_evaluation_draft: undefined // Also clear legacy field
                }
            };
        }
        case 'NEXT_INTERFACE':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    current_index: state.session.current_index + 1
                }
            };
        case 'PREV_INTERFACE':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    current_index: Math.max(0, state.session.current_index - 1)
                }
            };
        case 'END_SESSION':
            localStorage.removeItem(STORAGE_KEY);
            return { ...state, session: null, isLoading: false };
        default:
            return state;
    }
}

interface ExpertContextValue {
    state: ExpertState;
    createProvisionalSession: (recoveryCode: string) => Promise<boolean>;
    updateProfileField: (field: keyof ExpertProfile, value: ExpertProfile[keyof ExpertProfile]) => void;
    completeProfile: (profile: ExpertProfile) => Promise<boolean>;
    startSession: (profile: ExpertProfile, provisionalCode?: string) => Promise<boolean>;
    recoverSession: (recoveryCode: string) => Promise<boolean>;
    checkDeviceSession: () => Promise<ExpertSession | null>;
    updateEvaluationDraft: (ratings: Partial<IQAIRatings>, comments?: string) => void;
    getEvaluationDraft: () => EvaluationDraft | null;
    submitEvaluation: (ratings: IQAIRatings, comments?: string, timeSpent?: number, interactionData?: IQAIEvaluation['interaction_metadata']) => void;
    submitFinalEvaluation: (ratings: IQAIRatings, comments?: string, timeSpent?: number, interactionData?: IQAIEvaluation['interaction_metadata']) => Promise<boolean>;
    getCurrentInterface: () => InterfaceToEvaluate | null;
    isLastInterface: () => boolean;
    prevInterface: () => void;
    getProgress: () => { current: number; total: number };
    getRecoveryCode: () => string | null;
    getDeviceFingerprint: () => string;
    endSession: () => void;
    lastSaved: Date | null;
    isSaving: boolean;
}

const ExpertContext = createContext<ExpertContextValue | undefined>(undefined);


// Real executions are loaded via getSuccessfulExecutions from firestore-proxy

const APP_VERSION = '1.0.0';

export function ExpertProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(expertReducer, {
        session: null,
        isLoading: false,
        error: null,
        initialized: false
    });

    // Auto-save state
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Restore session from localStorage
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const session = JSON.parse(stored);
                dispatch({ type: 'START_SESSION', payload: session });
            } catch {
                localStorage.removeItem(STORAGE_KEY);
            }
        }
        // Mark as initialized regardless of whether session was found
        dispatch({ type: 'SET_INITIALIZED', payload: true });
    }, []);

    // Save session to localStorage (immediate)
    useEffect(() => {
        if (state.session) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state.session));
        }
    }, [state.session]);

    // Refresh interface translations from Firestore (always refresh to ensure latest data)
    useEffect(() => {
        const refreshTranslations = async () => {
            if (!state.session || !state.initialized) return;
            if (state.session.interfaces_to_evaluate.length === 0) return;

            // Always refresh to ensure we have the latest translations from Firestore
            // This fixes issues where localStorage has stale/corrupted data

            console.log('Refreshing interface translations from Firestore...');

            try {
                const executionsResult = await getSuccessfulExecutions(32);
                if (!executionsResult.success || !executionsResult.data) return;

                const executionMap = new Map<string, { ui_spec_pt?: object; ui_spec_en?: object }>();
                for (const exec of executionsResult.data) {
                    const parseSpec = (spec: string | object | undefined) => {
                        if (!spec) return undefined;
                        return typeof spec === 'string' ? JSON.parse(spec) : spec;
                    };
                    executionMap.set(exec.execution_id, {
                        ui_spec_pt: parseSpec(exec.ui_spec_pt),
                        ui_spec_en: parseSpec(exec.ui_spec_en)
                    });
                }

                // Update interfaces with translations (always prefer Firestore data over local)
                const updatedInterfaces = state.session.interfaces_to_evaluate.map(iface => {
                    const translations = executionMap.get(iface.execution_id);
                    if (translations) {
                        return {
                            ...iface,
                            // Always use Firestore data if available (fixes stale/corrupted localStorage)
                            ui_spec_pt: translations.ui_spec_pt ?? iface.ui_spec_pt ?? iface.ui_spec,
                            ui_spec_en: translations.ui_spec_en ?? iface.ui_spec_en
                        };
                    }
                    return iface;
                });

                // Only update if there are actual changes
                const hasChanges = updatedInterfaces.some(
                    (iface, idx) => iface.ui_spec_en !== state.session!.interfaces_to_evaluate[idx].ui_spec_en
                );

                if (hasChanges) {
                    const updatedSession = {
                        ...state.session,
                        interfaces_to_evaluate: updatedInterfaces
                    };
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
                    dispatch({ type: 'START_SESSION', payload: updatedSession });
                    console.log('Interface translations refreshed successfully');
                }
            } catch (error) {
                console.warn('Failed to refresh translations:', error);
            }
        };

        refreshTranslations();
    }, [state.session?.expert_id, state.initialized]); // Only run when session ID changes or on init

    // Auto-save to Firestore with debounce
    useEffect(() => {
        if (!state.session) return;

        // Clear previous timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce: save after 1 second of no changes
        saveTimeoutRef.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                // Update last_activity_at before saving
                const sessionToSave = {
                    ...state.session,
                    last_activity_at: new Date().toISOString(),
                };
                await saveExpertSession(sessionToSave as FirestoreExpertSession);
                setLastSaved(new Date());
                console.log('Session auto-saved to Firestore');
            } catch (error) {
                console.error('Failed to auto-save session:', error);
            } finally {
                setIsSaving(false);
            }
        }, 1000);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [state.session]);

    // Create a provisional session with just the recovery code (no profile yet)
    // If a session with this code already exists in Firestore, recover it instead
    const createProvisionalSession = useCallback(async (recoveryCode: string): Promise<boolean> => {
        // Check if we already have a session with this code in local state
        if (state.session?.recovery_code === recoveryCode) {
            return true; // Already have this session
        }

        // IMPORTANT: Check if a session with this code already exists in Firestore
        // This prevents overwriting existing sessions when the user returns with the same provisional code
        const existingResult = await getExpertSessionByCode(recoveryCode);
        if (existingResult.success && existingResult.data) {
            const existingSession = existingResult.data as ExpertSession;
            console.log('Found existing session for code:', recoveryCode, 'profile_status:', existingSession.profile_status);

            // Update device tracking for the recovered session
            const currentFingerprint = getStableFingerprint();
            const existingDevice = existingSession.devices?.find(d => d.fingerprint === currentFingerprint);

            let updatedSession: ExpertSession;
            if (existingDevice) {
                // Update last_seen_at for existing device
                updatedSession = {
                    ...existingSession,
                    devices: existingSession.devices.map(d =>
                        d.fingerprint === currentFingerprint
                            ? updateDeviceRecord(d)
                            : d
                    ),
                    last_activity_at: new Date().toISOString(),
                    metadata: {
                        ...existingSession.metadata,
                        created_from: 'recovered',
                        recovery_count: (existingSession.metadata?.recovery_count || 0) + 1,
                        last_device_fingerprint: currentFingerprint,
                    },
                };
            } else {
                // Add new device to the list
                const newDevice = createDeviceRecord();
                updatedSession = {
                    ...existingSession,
                    devices: [...(existingSession.devices || []), newDevice],
                    last_activity_at: new Date().toISOString(),
                    metadata: {
                        ...existingSession.metadata,
                        created_from: 'recovered',
                        recovery_count: (existingSession.metadata?.recovery_count || 0) + 1,
                        last_device_fingerprint: currentFingerprint,
                    },
                };
            }

            // Save updated session to Firestore
            await saveExpertSession(updatedSession as FirestoreExpertSession);

            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
            dispatch({ type: 'START_SESSION', payload: updatedSession });
            console.log('Recovered existing session:', recoveryCode);
            return true;
        }

        // No existing session found - create a new provisional session
        const deviceRecord = createDeviceRecord();
        const now = new Date().toISOString();

        const provisionalSession: ExpertSession = {
            expert_id: `expert_${Date.now()}`,
            recovery_code: recoveryCode,
            devices: [deviceRecord],
            profile: {}, // Empty profile - to be filled incrementally
            profile_status: 'incomplete',
            interfaces_to_evaluate: [], // Will be loaded when profile is complete
            current_index: 0,
            evaluations: [],
            status: 'in_progress',
            started_at: now,
            last_activity_at: now,
            metadata: {
                app_version: APP_VERSION,
                created_from: 'provisional',
                recovery_count: 0,
                last_device_fingerprint: deviceRecord.fingerprint,
            },
        };

        // Save to Firestore immediately
        const result = await saveExpertSession(provisionalSession as FirestoreExpertSession);
        if (!result.success) {
            console.error('Failed to create provisional session:', result.error);
            return false;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(provisionalSession));
        dispatch({ type: 'START_SESSION', payload: provisionalSession });
        console.log('Provisional session created:', recoveryCode);
        return true;
    }, [state.session]);

    // Update a single profile field (for incremental auto-save)
    const updateProfileField = useCallback((
        field: keyof ExpertProfile,
        value: ExpertProfile[keyof ExpertProfile]
    ) => {
        if (!state.session) return;
        dispatch({ type: 'UPDATE_PROFILE', payload: { [field]: value } });
    }, [state.session]);

    // Update evaluation draft (for auto-saving ratings and comments)
    const updateEvaluationDraft = useCallback((
        ratings: Partial<IQAIRatings>,
        comments?: string
    ) => {
        if (!state.session) return;
        const draft: EvaluationDraft = {
            interface_index: state.session.current_index,
            ratings,
            comments,
            last_updated: new Date().toISOString()
        };
        dispatch({ type: 'UPDATE_EVALUATION_DRAFT', payload: draft });
    }, [state.session]);

    // Get current evaluation draft (if for the current interface)
    const getEvaluationDraft = useCallback((): EvaluationDraft | null => {
        if (!state.session) return null;
        const currentIdx = state.session.current_index;

        // First, check the new evaluation_drafts map
        const draft = state.session.evaluation_drafts?.[currentIdx];
        if (draft) {
            return draft;
        }

        // Fallback: check legacy current_evaluation_draft for backward compatibility
        if (state.session.current_evaluation_draft &&
            state.session.current_evaluation_draft.interface_index === currentIdx) {
            return state.session.current_evaluation_draft;
        }

        return null;
    }, [state.session]);

    // Complete the profile and load interfaces for evaluation
    const completeProfile = useCallback(async (profile: ExpertProfile): Promise<boolean> => {
        if (!state.session) return false;

        dispatch({ type: 'SET_LOADING', payload: true });

        // Load interfaces to evaluate
        let interfaces: InterfaceToEvaluate[] = [];

        try {
            const executionsResult = await getSuccessfulExecutions(32);

            if (executionsResult.success && executionsResult.data && executionsResult.data.length > 0) {
                const testCasePromises = executionsResult.data.map(exec => getTestCase(exec.case_id));
                const testCaseResults = await Promise.all(testCasePromises);

                const mappedInterfaces: (InterfaceToEvaluate | null)[] = executionsResult.data.map((exec, index) => {
                    const testCaseResult = testCaseResults[index];
                    if (testCaseResult.success && testCaseResult.data) {
                        const testCase = testCaseResult.data;
                        const parseSpec = (spec: string | object | undefined) => {
                            if (!spec) return undefined;
                            return typeof spec === 'string' ? JSON.parse(spec) : spec;
                        };

                        const uiSpec = parseSpec(exec.ui_spec);
                        if (!uiSpec) return null;

                        return {
                            execution_id: exec.execution_id,
                            profile_id: testCase.profile.profile_id,
                            task_type: testCase.task_type,
                            process_phase: testCase.process_phase,
                            ui_spec: uiSpec,
                            ui_spec_pt: parseSpec(exec.ui_spec_pt),
                            ui_spec_en: parseSpec(exec.ui_spec_en),
                        } as InterfaceToEvaluate;
                    }
                    return null;
                });

                interfaces = mappedInterfaces.filter((i): i is InterfaceToEvaluate => i !== null);
            }
        } catch (error) {
            console.warn('Failed to load executions:', error);
        }

        if (interfaces.length === 0) {
            dispatch({ type: 'SET_ERROR', payload: 'Não há interfaces geradas disponíveis para avaliação.' });
            return false;
        }

        // Shuffle and limit
        const maxInterfaces = Math.min(16, interfaces.length);
        const shuffled = interfaces.sort(() => Math.random() - 0.5).slice(0, maxInterfaces);

        // Update session with complete profile and interfaces
        const updatedSession: ExpertSession = {
            ...state.session,
            profile: { ...profile, is_qualified: isQualifiedExpert(profile) },
            profile_status: 'complete',
            interfaces_to_evaluate: shuffled,
            last_activity_at: new Date().toISOString(),
            metadata: {
                ...state.session.metadata,
                created_from: 'new', // Update from 'provisional' to 'new'
            },
        };

        // Save to Firestore
        const result = await saveExpertSession(updatedSession as FirestoreExpertSession);
        if (!result.success) {
            dispatch({ type: 'SET_ERROR', payload: 'Falha ao salvar perfil.' });
            return false;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
        dispatch({ type: 'START_SESSION', payload: updatedSession });
        dispatch({ type: 'SET_LOADING', payload: false });
        return true;
    }, [state.session]);

    const startSession = useCallback(async (profile: ExpertProfile, provisionalCode?: string) => {
        dispatch({ type: 'SET_LOADING', payload: true });

        let interfaces: InterfaceToEvaluate[] = [];

        // Load real executions and their corresponding test cases
        try {
            const executionsResult = await getSuccessfulExecutions(32);

            if (executionsResult.success && executionsResult.data && executionsResult.data.length > 0) {
                const testCasePromises = executionsResult.data.map(exec => getTestCase(exec.case_id));
                const testCaseResults = await Promise.all(testCasePromises);

                const mappedInterfaces: (InterfaceToEvaluate | null)[] = executionsResult.data.map((exec, index) => {
                    const testCaseResult = testCaseResults[index];
                    if (testCaseResult.success && testCaseResult.data) {
                        const testCase = testCaseResult.data;
                        const parseSpec = (spec: string | object | undefined) => {
                            if (!spec) return undefined;
                            return typeof spec === 'string' ? JSON.parse(spec) : spec;
                        };

                        const uiSpec = parseSpec(exec.ui_spec);
                        if (!uiSpec) return null; // spec required

                        return {
                            execution_id: exec.execution_id,
                            profile_id: testCase.profile.profile_id,
                            task_type: testCase.task_type,
                            process_phase: testCase.process_phase,
                            ui_spec: uiSpec,
                            ui_spec_pt: parseSpec(exec.ui_spec_pt),
                            ui_spec_en: parseSpec(exec.ui_spec_en),
                        } as InterfaceToEvaluate;
                    }
                    return null;
                });

                interfaces = mappedInterfaces.filter((i): i is InterfaceToEvaluate => i !== null);

                console.log(`Loaded ${interfaces.length} successful executions with full test case data.`);
            } else {
                console.warn('No successful executions found');
            }
        } catch (error) {
            console.warn('Failed to load executions or test cases:', error);
        }

        if (interfaces.length === 0) {
            dispatch({ type: 'SET_ERROR', payload: 'Não há interfaces geradas disponíveis para avaliação. Execute alguns casos de teste na Dashboard primeiro.' });
            return false;
        }

        // Shuffle and limit based on available interfaces
        const maxInterfaces = Math.min(16, interfaces.length);
        const shuffled = interfaces.sort(() => Math.random() - 0.5).slice(0, maxInterfaces);

        // Use provisional code if provided, otherwise generate new one
        const recoveryCode = provisionalCode || generateRecoveryCode();
        const deviceRecord = createDeviceRecord();
        const now = new Date().toISOString();

        const session: ExpertSession = {
            expert_id: `expert_${Date.now()}`,
            recovery_code: recoveryCode,
            devices: [deviceRecord],
            profile,
            profile_status: 'complete',
            interfaces_to_evaluate: shuffled,
            current_index: 0,
            evaluations: [],
            status: 'in_progress',
            started_at: now,
            last_activity_at: now,
            metadata: {
                app_version: APP_VERSION,
                created_from: 'new',
                recovery_count: 0,
                last_device_fingerprint: deviceRecord.fingerprint,
            },
        };

        // Salvar sessão no Firestore
        const result = await saveExpertSession(session);
        if (!result.success) {
            dispatch({ type: 'SET_ERROR', payload: 'Falha ao salvar a sessão. Tente novamente.' });
            return false;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        dispatch({ type: 'START_SESSION', payload: session });
        return true;
    }, []);

    const recoverSession = useCallback(async (recoveryCode: string): Promise<boolean> => {
        dispatch({ type: 'SET_LOADING', payload: true });

        const result = await getExpertSessionByCode(recoveryCode);

        if (result.success && result.data) {
            const recoveredSession = result.data as ExpertSession;
            const currentFingerprint = getStableFingerprint();

            // Check if this device is already registered
            const existingDevice = recoveredSession.devices?.find(d => d.fingerprint === currentFingerprint);

            let updatedSession: ExpertSession;
            if (existingDevice) {
                // Update last_seen_at for existing device
                updatedSession = {
                    ...recoveredSession,
                    devices: recoveredSession.devices.map(d =>
                        d.fingerprint === currentFingerprint
                            ? updateDeviceRecord(d)
                            : d
                    ),
                    last_activity_at: new Date().toISOString(),
                    metadata: {
                        ...recoveredSession.metadata,
                        recovery_count: (recoveredSession.metadata?.recovery_count || 0) + 1,
                        last_device_fingerprint: currentFingerprint,
                    },
                };
            } else {
                // Add new device to the list
                const newDevice = createDeviceRecord();
                updatedSession = {
                    ...recoveredSession,
                    devices: [...(recoveredSession.devices || []), newDevice],
                    last_activity_at: new Date().toISOString(),
                    metadata: {
                        ...recoveredSession.metadata,
                        created_from: 'recovered',
                        recovery_count: (recoveredSession.metadata?.recovery_count || 0) + 1,
                        last_device_fingerprint: currentFingerprint,
                    },
                };
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
            dispatch({ type: 'START_SESSION', payload: updatedSession });
            return true;
        } else {
            dispatch({ type: 'SET_ERROR', payload: 'Código de recuperação não encontrado.' });
            return false;
        }
    }, []);

    // Check if there's a session for this device (auto-recovery)
    const checkDeviceSession = useCallback(async (): Promise<ExpertSession | null> => {
        const fingerprint = getStableFingerprint();
        const result = await getExpertSessionByDevice(fingerprint);

        if (result.success && result.data) {
            return result.data as ExpertSession;
        }
        return null;
    }, []);

    const submitEvaluation = useCallback((
        ratings: IQAIRatings,
        comments?: string,
        timeSpent: number = 0,
        interactionData?: IQAIEvaluation['interaction_metadata']
    ) => {
        if (!state.session) return;

        const currentInterface = state.session.interfaces_to_evaluate[state.session.current_index];

        // Calculate IQAI score
        const iqaiScore = calculateIQAI(ratings as FirestoreIQAIRatings);

        const evaluation: IQAIEvaluation = {
            execution_id: currentInterface.execution_id,
            profile_id: currentInterface.profile_id,
            task_type: currentInterface.task_type,
            process_phase: currentInterface.process_phase,
            ratings,
            iqai_score: iqaiScore,
            comments,
            evaluated_at: new Date().toISOString(),
            time_spent_seconds: timeSpent,
            interaction_metadata: interactionData,
        };

        dispatch({ type: 'ADD_EVALUATION', payload: evaluation });
        dispatch({ type: 'NEXT_INTERFACE' });
        // Scroll to top when navigating to next interface
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [state.session]);

    // Submit the final evaluation and mark session as completed
    // This saves immediately to Firestore (no debounce) to prevent data loss
    const submitFinalEvaluation = useCallback(async (
        ratings: IQAIRatings,
        comments?: string,
        timeSpent: number = 0,
        interactionData?: IQAIEvaluation['interaction_metadata']
    ): Promise<boolean> => {
        if (!state.session) return false;

        const currentInterface = state.session.interfaces_to_evaluate[state.session.current_index];

        // Calculate IQAI score
        const iqaiScore = calculateIQAI(ratings as FirestoreIQAIRatings);

        const evaluation: IQAIEvaluation = {
            execution_id: currentInterface.execution_id,
            profile_id: currentInterface.profile_id,
            task_type: currentInterface.task_type,
            process_phase: currentInterface.process_phase,
            ratings,
            iqai_score: iqaiScore,
            comments,
            evaluated_at: new Date().toISOString(),
            time_spent_seconds: timeSpent,
            interaction_metadata: interactionData,
        };

        // Create the completed session with the final evaluation
        const now = new Date().toISOString();
        const completedSession: ExpertSession = {
            ...state.session,
            evaluations: [...state.session.evaluations, evaluation],
            current_index: state.session.current_index + 1,
            current_evaluation_draft: undefined,
            evaluation_drafts: {}, // Clear all drafts when session is completed
            status: 'completed',
            completed_at: now,
            last_activity_at: now,
        };

        // Save immediately to Firestore (bypass debounce)
        setIsSaving(true);
        try {
            const result = await saveExpertSession(completedSession as FirestoreExpertSession);
            if (!result.success) {
                console.error('Failed to save completed session:', result.error);
                setIsSaving(false);
                return false;
            }
            console.log('Session completed and saved to Firestore');
            setLastSaved(new Date());

            // Update local state
            localStorage.setItem(STORAGE_KEY, JSON.stringify(completedSession));
            dispatch({ type: 'START_SESSION', payload: completedSession });

            return true;
        } catch (error) {
            console.error('Error saving completed session:', error);
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [state.session]);

    const prevInterface = useCallback(() => {
        dispatch({ type: 'PREV_INTERFACE' });
        // Scroll to top when navigating to previous interface
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const getCurrentInterface = useCallback((): InterfaceToEvaluate | null => {
        if (!state.session) return null;
        return state.session.interfaces_to_evaluate[state.session.current_index] || null;
    }, [state.session]);

    const isLastInterface = useCallback((): boolean => {
        if (!state.session) return false;
        return state.session.current_index >= state.session.interfaces_to_evaluate.length - 1;
    }, [state.session]);

    const getProgress = useCallback(() => {
        if (!state.session) return { current: 0, total: 0 };
        const total = state.session.interfaces_to_evaluate.length;
        const current = Math.min(state.session.current_index + 1, total);
        return { current, total };
    }, [state.session]);

    const endSession = useCallback(() => {
        dispatch({ type: 'END_SESSION' });
    }, []);

    const getRecoveryCode = useCallback((): string | null => {
        return state.session?.recovery_code || null;
    }, [state.session]);

    const getDeviceFingerprint = useCallback((): string => {
        return getStableFingerprint();
    }, []);

    return (
        <ExpertContext.Provider value={{
            state,
            createProvisionalSession,
            updateProfileField,
            completeProfile,
            startSession,
            recoverSession,
            checkDeviceSession,
            updateEvaluationDraft,
            getEvaluationDraft,
            submitEvaluation,
            submitFinalEvaluation,
            getCurrentInterface,
            isLastInterface,
            prevInterface,
            getProgress,
            getRecoveryCode,
            getDeviceFingerprint,
            endSession,
            lastSaved,
            isSaving,
        }}>
            {children}
        </ExpertContext.Provider>
    );
}

export function useExpert() {
    const context = useContext(ExpertContext);
    if (!context) {
        throw new Error('useExpert must be used within an ExpertProvider');
    }
    return context;
}
